import { z } from 'zod';
import { pcmpActionPlanKeySchema, pcmpRequestCategorySchema } from './pcmpRequestCategory.types';
import { PCMP_CHANNELS } from './pcmpProposal.types';
import {
  pcmpPageMetaSchema,
  pcmpQueueItemSchema,
  pcmpQueuePersonSchema,
  pcmpQueueSearchShape,
  pcmpSkippedProposalSchema,
  type PcmpQueueFilters,
} from './pcmpQueue.types';

export const PCMP_REQUEST_STATUSES = ['PENDING', 'PROCESSING', 'APPROVED', 'REJECTED'] as const;
export const pcmpRequestStatusSchema = z.enum(PCMP_REQUEST_STATUSES);
export type PcmpRequestStatus = z.infer<typeof pcmpRequestStatusSchema>;

/** The queue row of a request screen: the shared row plus who pays today. */
export const pcmpRequestQueueItemSchema = pcmpQueueItemSchema.extend({
  paymentSource: z.enum(PCMP_CHANNELS),
});

export type PcmpRequestQueueItem = z.infer<typeof pcmpRequestQueueItemSchema>;

/** One request, as My Requests and the Requested Proposals tabs show it. */
export const pcmpRequestSummarySchema = z.object({
  requestId: z.coerce.number(),
  categoryId: z.coerce.number(),
  categoryCode: z.string(),
  categoryName: z.string(),
  actionPlanKey: pcmpActionPlanKeySchema.nullable(),
  actionPlanLabel: z.string(),
  paymentSource: z.enum(PCMP_CHANNELS),
  status: pcmpRequestStatusSchema,
  note: z.string().nullable(),
  raisedAt: z.string(),
  raisedBy: pcmpQueuePersonSchema.nullable(),
  processingAt: z.string().nullable(),
  processingBy: pcmpQueuePersonSchema.nullable(),
  decidedAt: z.string().nullable(),
  decidedBy: pcmpQueuePersonSchema.nullable(),
  decisionComment: z.string().nullable(),
  executedAt: z.string().nullable(),
});

export type PcmpRequestSummary = z.infer<typeof pcmpRequestSummarySchema>;

export const pcmpRequestMineItemSchema = pcmpRequestQueueItemSchema.extend({
  request: pcmpRequestSummarySchema,
});

export type PcmpRequestMineItem = z.infer<typeof pcmpRequestMineItemSchema>;

export const paginatedPcmpRequestQueueSchema = z.object({
  data: z.array(pcmpRequestQueueItemSchema),
  meta: pcmpPageMetaSchema,
});

export const paginatedPcmpRequestMineSchema = z.object({
  data: z.array(pcmpRequestMineItemSchema),
  meta: pcmpPageMetaSchema,
});

export type PaginatedPcmpRequestQueue = z.infer<typeof paginatedPcmpRequestQueueSchema>;
export type PaginatedPcmpRequestMine = z.infer<typeof paginatedPcmpRequestMineSchema>;

export const pcmpRequestCountsSchema = z.object({
  standard: z.number(),
  /** The proposed total of the whole Standard tab, for "select all matching". */
  standardAmount: z.number(),
  disbursed: z.number(),
  disbursedAmount: z.number(),
  /** My requests nobody has decided yet. */
  mine: z.number(),
  batches: z.number(),
  /** Every Urgent Payment request I raised, any status (LB-1201). */
  urgentRequests: z.number(),
});

export type PcmpRequestCounts = z.infer<typeof pcmpRequestCountsSchema>;

/** The list tabs the queue endpoint serves; the Batches tab reads the urgent batches. */
export const PCMP_REQUEST_LIST_TABS = ['STANDARD', 'DISBURSED', 'MINE'] as const;
export type PcmpRequestListTab = (typeof PCMP_REQUEST_LIST_TABS)[number];

export const PCMP_REQUEST_VIEWS = [
  'STANDARD',
  'DISBURSED',
  'MINE',
  'BATCHES',
  'URGENT_REQUESTS',
] as const;
export type PcmpRequestView = (typeof PCMP_REQUEST_VIEWS)[number];

export interface PcmpRequestQueueParams extends PcmpQueueFilters {
  tab: PcmpRequestListTab;
  /** My Requests only. */
  requestStatus?: PcmpRequestStatus | undefined;
  /** My Requests only: a request category code (LDS-768). */
  requestCategory?: string | undefined;
  page: number;
  limit: number;
}

export interface PcmpRequestBatchesParams extends PcmpQueueFilters {
  page: number;
  limit: number;
}

export const pcmpRequestQueueSearchSchema = z.object({
  tab: z.enum(PCMP_REQUEST_VIEWS).optional().catch(undefined),
  requestStatus: pcmpRequestStatusSchema.optional().catch(undefined),
  requestCategory: z.string().optional().catch(undefined),
  ...pcmpQueueSearchShape,
});

/** Urgent Requests: the page, narrowed to one Urgent Payment category (LDS-768). */
export interface PcmpUrgentRequestsParams extends PcmpRequestBatchesParams {
  requestCategory?: string | undefined;
}

export type PcmpRequestQueueSearch = z.infer<typeof pcmpRequestQueueSearchSchema>;

export const pcmpUrgentBatchSearchSchema = z.object(pcmpQueueSearchShape);

export type PcmpUrgentBatchSearch = z.infer<typeof pcmpUrgentBatchSearchSchema>;

export const pcmpRequestCategoriesListSchema = z.array(pcmpRequestCategorySchema);

/** The picked ids, or everything the tab lists under the filters. */
export type PcmpRequestSelection =
  | { proposalIds: number[] }
  | { selectAllMatching: true; filters: PcmpQueueFilters; excludedProposalIds: number[] };

/** What a Payment Return request takes back from one proposal; no amount is everything left. */
export interface PcmpRequestReturnItem {
  proposalId: number;
  amount?: number | undefined;
  /** The deposit slip: required for an Accounts payout, optional for a Marketing one. */
  slipKey?: string | undefined;
}

export type PcmpRequestSubmitPayload = {
  categoryId: number;
  /** Required, except on a Payment Return of Marketing payouts (LDS-766). */
  comment?: string | undefined;
  /** Payment Return only: one entry per proposal in proposalIds. */
  returns?: PcmpRequestReturnItem[] | undefined;
} & PcmpRequestSelection;

export const pcmpRequestSubmitResultSchema = z.object({
  category: z.object({ id: z.coerce.number(), code: z.string(), name: z.string() }),
  requested: z.number(),
  submitted: z.array(z.object({ proposalId: z.coerce.number(), requestId: z.coerce.number() })),
  skipped: z.array(pcmpSkippedProposalSchema),
});

export type PcmpRequestSubmitResult = z.infer<typeof pcmpRequestSubmitResultSchema>;
